import { useState } from 'react'

import { StorefrontModal } from '@/components/storefronts/StorefrontModal'
import { PricingPreviewDialog } from '@/components/ymarket-pricing/PricingPreviewDialog'
import { useSelectedYmOffers } from '@/hooks/useSelectedYmOffers'
import type { InventoryFilters } from '@/lib/api/client/inventory'
import type { YmReconcileDirection } from '@/lib/api/schemas/ymarket-pricing.schema'
import type { SelectionScope } from '@/lib/views/actions'
import { RECONCILE_EXPLANATIONS, RECONCILE_LABELS } from '@/lib/ymarket-pricing'

const DIRECTIONS: readonly YmReconcileDirection[] = ['forge_to_ym', 'ym_to_forge']

/** «Сверить цены»: one direction for the selected offers, previewed and applied by the YM pricing writer. */
export function YmarketReconcileDialog({
  selection,
  onDone,
}: {
  selection: SelectionScope<InventoryFilters>
  onDone: () => void
}) {
  const [direction, setDirection] = useState<YmReconcileDirection | null>(null)
  const productIds = selection.kind === 'ids' ? selection.ids : null
  const resolution = useSelectedYmOffers(productIds)
  const selectedCount = selection.kind === 'ids' ? selection.ids.length : selection.expectedTotal

  if (direction !== null && resolution.kind === 'ready') {
    return (
      <PricingPreviewDialog
        request={{ reconcile: { offerIds: resolution.offers.map((offer) => offer.offerId), direction } }}
        offers={resolution.offers}
        title={`${RECONCILE_LABELS[direction]} · ${resolution.offers.length}`}
        marketplaces="Яндекс Маркет"
        onClose={(applied) => (applied ? onDone() : setDirection(null))}
      />
    )
  }

  const offers = resolution.kind === 'ready' ? resolution.offers : []

  return (
    <StorefrontModal title={`Сверить цены · ${selectedCount} изделий`} labelledBy="ymarket-reconcile-title" onClose={onDone} footer={(
      <button type="button" className="btn btn-ghost" onClick={onDone}>Закрыть</button>
    )}>
      <div className="space-y-4 text-sm">
        {productIds === null ? (
          <p role="alert" className="rounded-md bg-bone/60 p-3 text-ink-2">Сверка работает по отмеченным изделиям: выбор по фильтру здесь не поддерживается, отметьте изделия в таблице.</p>
        ) : null}
        {resolution.kind === 'loading' ? <p role="status" className="text-ink-muted">Ищем предложения на Маркете…</p> : null}
        {resolution.kind === 'failed' ? <p role="alert" className="rounded-md bg-st-unpaid/10 p-3 text-st-unpaid">{resolution.message}</p> : null}
        {resolution.kind === 'ready' ? (
          <>
            <p className="font-semibold tab-nums">Найдено предложений на Маркете: {offers.length} из {selectedCount}</p>
            {offers.length < selectedCount ? <p className="text-ink-muted">Остальные изделия не опубликованы на Яндекс Маркете — их цены не сверяются.</p> : null}
            <div className="grid gap-2">
              {DIRECTIONS.map((candidate) => (
                <button key={candidate} type="button" className="btn btn-ghost justify-start text-left" disabled={offers.length === 0} onClick={() => setDirection(candidate)}>
                  <span>
                    <span className="block font-semibold">{RECONCILE_LABELS[candidate]}</span>
                    <span className="block text-[12px] font-normal text-ink-muted">{RECONCILE_EXPLANATIONS[candidate]}</span>
                  </span>
                </button>
              ))}
            </div>
          </>
        ) : null}
      </div>
    </StorefrontModal>
  )
}
